import { POS_PAGE_SIZE } from '../constants/posConst';
import '../css/Pos.css';

/**
 * Pager for the POS record lists — the sales preview and the audit trail.
 *
 * Owns no page state of its own: the list that renders the rows is the one
 * that has to slice them, so the current page lives there and this only
 * reports and requests changes. That keeps a single source of truth when a
 * screen resets the page for its own reasons (a changed filter, a fresh
 * fetch).
 *
 * Renders nothing for a single page, so a short list is not captioned with a
 * pager that can do nothing.
 *
 * @param {object} props
 * @param {number} props.page - current page, 1-based
 * @param {number} props.totalItems - length of the full (filtered) list
 * @param {(page: number) => void} props.onPageChange - called with the page to show
 * @param {number} [props.pageSize=POS_PAGE_SIZE] - rows per page
 * @returns {JSX.Element|null}
 */
function PosPager({ page, totalItems, onPageChange, pageSize = POS_PAGE_SIZE }) {
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  if (pageCount <= 1) return null;

  // Clamped rather than computed straight from `page`, so a caller that has
  // not yet reset its page after the list shrank still shows a sane range
  // instead of "Showing 31-30 of 12".
  const first = Math.min((page - 1) * pageSize + 1, totalItems);
  const last = Math.min(page * pageSize, totalItems);

  return (
    <div className="pos-pager">
      <span>Showing {first}&ndash;{last} of {totalItems}</span>
      <div className="pos-pager-btns">
        <button
          type="button"
          className="pos-btn pos-btn-ghost"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
        >
          Previous
        </button>
        <span className="pos-pager-page">Page {page} of {pageCount}</span>
        <button
          type="button"
          className="pos-btn pos-btn-ghost"
          onClick={() => onPageChange(Math.min(pageCount, page + 1))}
          disabled={page >= pageCount}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default PosPager;
